// PortfolioSpikeMap.jsx
import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as d3 from "d3";
import * as topojson from "topojson-client";

const PortfolioSpikeMap = ({ width = 800, height = 500, plantData = [] }) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const projectionRef = useRef(null);
  const tooltipRef = useRef(null);
  const navigate = useNavigate();

  // Draw static US map background once (or when width/height change)
  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const container = d3.select(containerRef.current);

    // Clear and rebuild the root svg content for width/height changes
    svg.selectAll("*").remove();
    container.selectAll(".spike-map-tooltip").remove();

    // Build base layers synchronously so updates can run immediately
    const bgLayer = svg.append("g").attr("class", "background-layer");
    svg.append("g").attr("class", "spike-layer");
    svg.append("g").attr("class", "label-layer");

    tooltipRef.current = container
      .append("div")
      .attr("class", "spike-map-tooltip")
      .attr("role", "tooltip");

    // Projection is available immediately for spikes/labels
    const projection = d3
      .geoAlbersUsa()
      .translate([width / 2, height / 2])
      .scale(width * 1.2);

    projectionRef.current = projection;
    const path = d3.geoPath().projection(projection);

    // Fetch and draw the states
    d3.json("/data/states-10m.json")
      .then((usData) => {
        const states = topojson.feature(usData, usData.objects.states);
        bgLayer
          .selectAll("path")
          .data(states.features)
          .join("path")
          .attr("d", path)
          .attr("fill", "#f0f0f0")
          .attr("stroke", "#999");
      })
      .catch((err) => console.error("Error loading US map data:", err));

    return () => {
      container.selectAll(".spike-map-tooltip").remove();
      tooltipRef.current = null;
    };
  }, [width, height]);

  // Update spikes and labels – runs on mount too, no need for data to change
  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const projection = projectionRef.current;
    if (!projection) return; // projection not ready (very unlikely here)

    const data = Array.isArray(plantData) ? plantData : [];

    const projectCoords = (coords) => {
      if (!Array.isArray(coords) || coords.length < 2) return null;
      return projection(coords);
    };

    const getPercentage = (d) => {
      const value = Number(d.production_pct);
      return Number.isFinite(value) ? value : 0;
    };

    // Capacity is a 0–100% factor; use a fixed domain so 0% stays flat.
    const spikeHeightScale = d3.scaleLinear().domain([0, 100]).range([0, 50]);
    const spikeWidthScale = d3.scaleLinear().domain([0, 100]).range([2, 8]);

    const getSpikeColor = (capacity) => {
      if (capacity <= 25) return "green";
      if (capacity <= 74) return "yellow";
      return "red";
    };

    // Ensure layers exist (in case future refactors move creation)
    const spikeLayer =
      !svg.select(".spike-layer").empty()
        ? svg.select(".spike-layer")
        : svg.append("g").attr("class", "spike-layer");

    const labelLayer =
      !svg.select(".label-layer").empty()
        ? svg.select(".label-layer")
        : svg.append("g").attr("class", "label-layer");

    // JOIN for spikes as triangles (polygons)
    const spikes = spikeLayer.selectAll("polygon").data(data, (d) => d.plant_name);

    // ENTER + UPDATE with initial attributes set before transition
    const spikesEnter = spikes
      .enter()
      .append("polygon")
      .attr("stroke", "#333")
      .attr("stroke-width", 0.5)
      // Set an initial tiny spike at its base (or skip if coords are null)
      .attr("points", (d) => {
        const coords = projectCoords(d.coords);
        if (!coords) return "";
        const [x, y] = coords;
        const baseWidth = Math.max(2, spikeWidthScale(getPercentage(d)));
        // Start collapsed at the base for a nice grow-in on first render
        return `${x - baseWidth / 2},${y} ${x + baseWidth / 2},${y} ${x},${y}`;
      })
      .attr("fill", (d) => getSpikeColor(getPercentage(d)));

    // Apply the final geometry to both new and existing elements
    spikesEnter
      .merge(spikes)
      .transition()
      .duration(800)
      .attr("points", (d) => {
        const coords = projectCoords(d.coords);
        if (!coords) return "";
        const [x, y] = coords;
        const height = spikeHeightScale(getPercentage(d));
        const baseWidth = Math.max(2, spikeWidthScale(getPercentage(d)));
        return `${x - baseWidth / 2},${y} ${x + baseWidth / 2},${y} ${x},${y - height}`;
      })
      .attr("fill", (d) => getSpikeColor(getPercentage(d)));

    // EXIT
    spikes.exit().remove();

    // JOIN for labels
    const labels = labelLayer.selectAll("text").data(data, (d) => d.plant_name);

    const labelsEnter = labels
      .enter()
      .append("text")
      .attr("font-size", "12px")
      .attr("fill", "#0077cc")
      .attr("dominant-baseline", "ideographic") // helps position above spike tip
      .style("cursor", (d) => (d.dashboard ? "pointer" : "default"))
      .text((d) => `${d.plant_name} (${getPercentage(d)}%)`)
      // Start near the base for a subtle rise on first render
      .attr("x", (d) => {
        const coords = projectCoords(d.coords);
        return coords ? coords[0] + 5 : 0;
      })
      .attr("y", (d) => {
        const coords = projectCoords(d.coords);
        return coords ? coords[1] - 5 : 0;
      });

    const showLabelTooltip = (event, d) => {
      const tooltip = tooltipRef.current;
      const container = containerRef.current;
      if (!tooltip || !container) return;
      const [x, y] = d3.pointer(event, container);
      const pct = getPercentage(d);
      const pctLabel = String(pct);
      if (tooltip.attr("data-plant") !== d.plant_name || tooltip.attr("data-pct") !== pctLabel) {
        tooltip.selectAll("*").remove();
        const body = tooltip.append("div").attr("class", "siteConditions");
        body.append("strong").text(d.plant_name);
        body.append("h2").text(`${pctLabel}%`);
        body.append("span").text("Production");
        tooltip.attr("data-plant", d.plant_name).attr("data-pct", pctLabel);
      }
      tooltip
        .style("opacity", 1)
        .style("left", `${x}px`)
        .style("top", `${y}px`);
    };

    const hideLabelTooltip = () => {
      tooltipRef.current?.style("opacity", 0);
    };

    labelsEnter
      .merge(labels)
      .on("click", (_event, d) => {
        if (d.dashboard) {
          navigate(d.dashboard);
        }
      })
      .on("mouseenter", function (event, d) {
        if (d.dashboard) {
          d3.select(this).style("text-decoration", "underline");
        }
        showLabelTooltip(event, d);
      })
      .on("mousemove", function (event, d) {
        showLabelTooltip(event, d);
      })
      .on("mouseleave", function (_event, d) {
        if (d.dashboard) {
          d3.select(this).style("text-decoration", "none");
        }
        hideLabelTooltip();
      })
      .transition()
      .duration(800)
      .attr("x", (d) => {
        const coords = projectCoords(d.coords);
        return coords ? coords[0] + 5 : 0;
      })
      .attr("y", (d) => {
        const coords = projectCoords(d.coords);
        const h = spikeHeightScale(getPercentage(d));
        return coords ? coords[1] - h - 5 : 0;
      })
      .tween("text", function (d) {
        // Update text during the transition in case capacity animates later
        const node = d3.select(this);
        return (t) => {
          node.text(`${d.plant_name} (${getPercentage(d)}%)`);
        };
      });

    labels.exit().remove();
  }, [plantData, width, height, navigate]);

  return (
    <div ref={containerRef} style={{ position: "relative", display: "block", textAlign: "center" }}>
      <svg ref={svgRef} width={width} height={height} />
    </div>
  );
};

export default PortfolioSpikeMap;